// File path: /core/frontend/src/services/retryOn503.ts
// C-503UI (S4 row 14): a 503 is retried once, then surfaces as a readable error.
//
// API Gateway answers 503 with Retry-After when the authorizer cannot reach the identity store
// within its resume budget (Aurora paused — contracts §1): the AUTHORIZER_FAILURE /
// AUTHORIZER_CONFIGURATION_ERROR gateway responses (scripts/deploy/gateway_responses_dev.py).
// Before this, the clients turned a 503 into empty "fallback" data, so a slow resume showed
// empty dashboards with no error. Now: wait Retry-After (default 5 s, at most 30 s), resend
// the same request once, and if that is a 503 as well, reject with the AxiosError so the page
// can show its error state.
//
// Why resending any method is safe: those 503s are produced before the integration runs, so the
// request — a POST or PUT included — never reached a handler, and no handler returns 503 itself
// (2026-10-08). If a handler ever does, after doing work, limit this retry to idempotent methods
// or to the AUTHORIZER_FAILURE code.
//
// Retry-After is read when the browser exposes it. Cross-origin it does so only if the response
// carries Access-Control-Expose-Headers: Retry-After; otherwise the default applies.

import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

export const DEFAULT_RETRY_AFTER_SECONDS = 5;
export const MAX_RETRY_AFTER_SECONDS = 30;
export const SERVICE_UNAVAILABLE_MESSAGE =
  'LumiLearn is temporarily unavailable. Please try again in a moment.';

type RetriableConfig = AxiosRequestConfig & { _retried503?: boolean };

/** The wait between the 503 and the retry. An object so tests can replace it. */
export const retryTimer = {
  sleep: (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms)),
};

/**
 * Seconds to wait for a Retry-After value. Only the delta-seconds form is read (the gateway
 * sends '5'); missing or anything else → the default; capped at MAX_RETRY_AFTER_SECONDS.
 */
export function retryAfterSeconds(value: unknown): number {
  const raw = Array.isArray(value) ? value[0] : value;
  const text = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
  if (!/^\d+(\.\d+)?$/.test(text)) return DEFAULT_RETRY_AFTER_SECONDS;
  return Math.min(Number(text), MAX_RETRY_AFTER_SECONDS);
}

/** A response header, case-insensitively, from AxiosHeaders or a plain object. */
function readHeader(headers: unknown, name: string): unknown {
  if (!headers || typeof headers !== 'object') return undefined;
  const h = headers as Record<string, unknown> & { get?: (header: string) => unknown };
  if (typeof h.get === 'function') return h.get(name);
  const key = Object.keys(h).find((k) => k.toLowerCase() === name.toLowerCase());
  return key === undefined ? undefined : h[key];
}

/**
 * For a response interceptor's error handler, after it has checked for a 503:
 * the first 503 of a request waits Retry-After and resends it through `client` once; a 503 on
 * that retry (or one with no request to resend) rejects with the same AxiosError, its message
 * replaced by SERVICE_UNAVAILABLE_MESSAGE. Anything that is not a 503 is rejected untouched.
 */
export async function retryOnceOn503(client: AxiosInstance, error: AxiosError): Promise<AxiosResponse> {
  if (error.response?.status !== 503) throw error;

  const config = error.config as RetriableConfig | undefined;
  if (!config || config._retried503) {
    error.message = SERVICE_UNAVAILABLE_MESSAGE;
    throw error;
  }

  config._retried503 = true;
  const seconds = retryAfterSeconds(readHeader(error.response.headers, 'Retry-After'));
  await retryTimer.sleep(seconds * 1000);
  return client.request(config);
}
